// EN-1196 unmerge confirm. Opened from the provenance popover, the visit menu or the Recycle Bin.
import { useState } from "react";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import HDDialog from "@/common/components/HDDialog";
import { useAppDispatch, useAppSelector, useAppStore } from "@/common/hooks/useRedux";
import { MergeBlockReason, MergeResult, postUnmerge } from "@/common/lib/api/visitMerges";
import { useInvalidateMergeState } from "@/state/hooks/useVisitMerges";
import { closeUnmergeConfirm } from "@/state/redux/mergeVisitsUiSlice";
import { useDataContextApi } from "@/state/providers/DataProvider";
import { fetchPatientData, getPatientData } from "@/features/patient-view/state/redux/activePatientDataSlice";
import { useMergeState } from "./MergeStatesProvider";
import { usePimsName } from "./usePimsName";
import { mergedToastId, unmergedToastId } from "./mergedToastId";
import { confirmedUnmerged, unmergeMayHaveWorked } from "./confirmUnmerge";
import { COPY, formatTime, labelTime, unmergeReasonText } from "./copy";

type Failure = Exclude<MergeResult, { ok: true }>;

export function UnmergeConfirm({
    mergeId,
    targetId,
    targetTime,
}: {
    mergeId: string;
    targetId: string;
    targetTime?: string;
}) {
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const invalidate = useInvalidateMergeState();
    const { refreshPatientRecords } = useDataContextApi();
    const pimsName = usePimsName();
    const { state } = useMergeState(targetId);
    // The kept visit's latest merge may be a later one (opened from the Recycle Bin); only describe this one.
    const m = state?.latestMerge?.mergeId === mergeId ? state.latestMerge : null;
    const time = m ? labelTime(m.sourceLabel) : "";
    // Opened from the kept visit's own page (menu, Details), the page has its time.
    const openVisit = useAppSelector(getPatientData);
    const keptTime =
        targetTime ??
        (openVisit?.id === targetId && openVisit.created_at
            ? formatTime(openVisit.scheduled_at ?? null, openVisit.created_at)
            : "");
    const [busy, setBusy] = useState(false);
    // reason null: a block reason this build does not know.
    const [blocked, setBlocked] = useState<{ reason: MergeBlockReason | null } | null>(null);
    const close = () => dispatch(closeUnmergeConfirm());

    // Read at completion: the vet may have cancelled and opened another visit while this ran.
    const reloadKeptIfOpen = () => {
        if (getPatientData(store.getState())?.id === targetId) dispatch(fetchPatientData(targetId));
    };

    const succeed = (audioUnconfirmed?: number) => {
        // Its Undo would unmerge a merge that no longer stands.
        toast.dismiss(mergedToastId(mergeId));
        invalidate();
        void refreshPatientRecords();
        reloadKeptIfOpen();
        const toastId = unmergedToastId(mergeId);
        if (audioUnconfirmed) toast.warning(COPY.unmergedAudioUnconfirmed(audioUnconfirmed), { toastId });
        else toast.success(time ? COPY.unmergedToast(time) : "Unmerged.", { toastId });
        // Leave a confirm opened since for another merge alone.
        dispatch(closeUnmergeConfirm(mergeId));
    };

    const fail = (r: Failure) => {
        // It may have committed after all: the vet is told to check the visit list, so make it current, and the
        // kept visit's page too (a late unmerge took clips and documents off it).
        const unconfirmed = unmergeMayHaveWorked(r);
        if (unconfirmed) {
            invalidate();
            void refreshPatientRecords();
            reloadKeptIfOpen();
        }
        toast.error(
            r.kind === "busy"
                ? COPY.busy
                : r.kind === "audio" && !r.retryable
                  ? COPY.unmergeNotAutomatic
                  : unconfirmed
                    ? COPY.undoUnconfirmed
                    : COPY.undoFailed,
        );
        // postUnmerge already reported an audio failure with the server's reason; busy is an expected answer.
        if (r.kind === "audio" || r.kind === "busy") return;
        Sentry.captureException(new Error(`Unmerge failed: ${r.kind}`), {
            tags: { errorType: "visit_unmerge", errorCategory: r.kind === "unknown" ? "connectivity" : "code_bug" },
        });
    };

    const confirm = async () => {
        if (busy) return;
        setBusy(true);
        setBlocked(null);
        const r = await postUnmerge(mergeId);
        const polled = !r.ok && unmergeMayHaveWorked(r) ? await confirmedUnmerged(mergeId, r) : null;
        setBusy(false);
        if (r.ok || polled) succeed(r.ok ? r.audioUnconfirmed : polled?.audioUnconfirmed);
        // A 409 (window passed, recording started) is an expected answer, not an error. The cached merge state
        // may still offer Unmerge (menu, Details, Recycle Bin), so reload it.
        else if (!r.ok && r.kind === "blocked") {
            setBlocked({ reason: r.reason });
            invalidate();
        } else if (!r.ok) fail(r);
    };

    const btn = "rounded-md px-3.5 py-2 text-sm font-semibold";
    return (
        <HDDialog
            isOpen
            handleClose={close}
            header={time ? COPY.unmergeTitle(time) : `${COPY.unmerge}?`}
            subHeader={m ? COPY.unmergeSubtitle(DateTime.fromISO(m.mergedAt).toFormat("MMM d, h:mm a")) : undefined}
            footer={
                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        className={`${btn} bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50`}
                        onClick={close}
                    >
                        {COPY.cancel}
                    </button>
                    {/* The window has passed or a later merge stands: asking again cannot work. */}
                    {blocked?.reason !== "unmerge_unavailable" && (
                        <button
                            type="button"
                            disabled={busy}
                            className={`${btn} bg-max-900 text-white hover:bg-max-950 disabled:opacity-60`}
                            onClick={confirm}
                        >
                            {COPY.unmerge}
                        </button>
                    )}
                </div>
            }
        >
            <div className="flex flex-col gap-3 text-sm">
                {m && COPY.movedCounts(m.moved) && <p>{`Moves back: ${COPY.movedCounts(m.moved)}`}</p>}
                {m?.exportedSinceMerge.map((header) => (
                    <p
                        key={header}
                        className="rounded-lg border border-tropicana-300 bg-tropicana-25 px-3 py-2 text-tropicana-950"
                    >
                        {COPY.unmergeExported(header, pimsName)}
                    </p>
                ))}
                <p className="text-gray-500">{COPY.unmergeTail(keptTime)}</p>
                {blocked && (
                    <p role="alert" className="rounded-lg border border-blaze-100 bg-blaze-25 px-3 py-2 text-blaze-900">
                        {unmergeReasonText(blocked.reason, pimsName)}
                    </p>
                )}
            </div>
        </HDDialog>
    );
}
